import { ApiError } from "@/common/lib/api";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { useLotsList } from "@/features/lots/api/lotsApi";
import { ChevronLeft } from "lucide-react";
import { Link, useParams } from "react-router-dom";
import { useCostAllocation } from "../api/costAllocationsApi";
import { AllocationsBreakdownTable } from "../components/AllocationsBreakdownTable";
import { categoryLabel } from "../utils/categories";
import { formatMoney } from "../utils/formatMoney";
import { formatPeriod } from "../utils/formatPeriod";
import { resolveLotLabel } from "../utils/resolveLotLabel";

export function CostAllocationDetailPage() {
  const { costId = "" } = useParams<{ costId: string }>();
  const { currentAccount } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";

  const detailQuery = useCostAllocation(accountId, costId);
  const lotsQuery = useLotsList(accountId);

  if (detailQuery.isLoading || lotsQuery.isLoading) {
    return (
      <>
        <title>Cargando · Costos · HATO</title>
        <Breadcrumb />
        <DetailSkeleton />
      </>
    );
  }

  if (detailQuery.isError) {
    if (detailQuery.error instanceof ApiError && detailQuery.error.status === 404) {
      return <CostNotFound />;
    }
    return (
      <>
        <title>Costos · HATO</title>
        <Breadcrumb />
        <div className="mt-12 flex flex-col items-center gap-4 text-center">
          <p className="text-app-ink-2 text-sm">No se pudo cargar el costo.</p>
          <button
            type="button"
            onClick={() => void detailQuery.refetch()}
            className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
          >
            Reintentar
          </button>
        </div>
      </>
    );
  }

  const detail = detailQuery.data;
  if (!detail) return <CostNotFound />;

  const { cost, allocations } = detail;
  const lot = lotsQuery.data?.lots.find((l) => l.id === cost.lotId);
  const lotLabel = resolveLotLabel({
    lotId: cost.lotId,
    lot,
    isLoading: lotsQuery.isPending,
    isError: lotsQuery.isError,
  });

  return (
    <>
      <title>{`${categoryLabel(cost.category)} · Costos · HATO`}</title>
      <Breadcrumb />

      <div className="mt-4 flex flex-col gap-6">
        <header>
          <div className="flex items-baseline gap-2">
            <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">
              {categoryLabel(cost.category)}
            </h1>
            <span className="text-app-ink-3 text-sm">{lotLabel}</span>
          </div>
          <p className="mt-1 text-app-ink-3 text-sm">
            {formatPeriod(cost.periodStart, cost.periodEnd)}
          </p>
        </header>

        <CostSummary cost={cost} />

        {cost.invoice && <InvoiceSection invoice={cost.invoice} />}

        <AllocationsBreakdownTable allocations={allocations} totalAmount={cost.amount} />
      </div>
    </>
  );
}

// B73 (AI-D25): sección read-only con los datos de factura capturados al
// registrar el costo (B67). Solo se renderiza si hay al menos un campo;
// los campos ausentes no muestran fila (nada de "—" en pantalla).
const DOC_TYPE_LABEL: Record<string, string> = {
  factura_electronica: "Factura electrónica",
  tiquete: "Tiquete",
  recibo: "Recibo",
  otro: "Otro",
};

function formatInvoiceDate(isoDate: string): string {
  // YYYY-MM-DD → fecha local es-CR sin rollover de TZ (mediodía local).
  const d = new Date(`${isoDate}T12:00:00`);
  return d.toLocaleDateString("es-CR", { day: "numeric", month: "short", year: "numeric" });
}

function ivaLabel(invoice: import("../api/costAllocationsApi").CostInvoiceDetail): string | null {
  const parts: string[] = [];
  if (invoice.ivaAmount !== null) parts.push(formatMoney(invoice.ivaAmount));
  if (invoice.ivaRate !== null)
    parts.push(invoice.ivaRate === "exento" ? "Exento" : `${invoice.ivaRate}%`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function InvoiceSection({
  invoice,
}: { invoice: import("../api/costAllocationsApi").CostInvoiceDetail }) {
  const iva = ivaLabel(invoice);
  return (
    <section
      aria-label="Datos de factura"
      className="rounded-2xl border border-app-border bg-app-surface p-4"
    >
      <h2 className="mb-3 font-medium text-[11px] text-app-ink-3 uppercase tracking-[0.06em]">
        Factura
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {invoice.invoiceNumber !== null && (
          <Field label="# Factura" value={invoice.invoiceNumber} mono />
        )}
        {invoice.docType !== null && (
          <Field
            label="Tipo de documento"
            value={DOC_TYPE_LABEL[invoice.docType] ?? invoice.docType}
          />
        )}
        {invoice.invoiceDate !== null && (
          <Field label="Fecha de emisión" value={formatInvoiceDate(invoice.invoiceDate)} />
        )}
        {invoice.supplierName !== null && <Field label="Proveedor" value={invoice.supplierName} />}
        {invoice.supplierId !== null && (
          <Field label="Cédula del proveedor" value={invoice.supplierId} mono />
        )}
        {iva !== null && <Field label="IVA" value={iva} />}
      </div>
    </section>
  );
}

function CostSummary({
  cost,
}: { cost: import("../api/costAllocationsApi").CostAllocationDetail["cost"] }) {
  const occurredAtDate = new Date(cost.occurredAt);
  return (
    <section
      aria-label="Resumen del costo"
      className="grid grid-cols-1 gap-4 rounded-2xl border border-app-border bg-app-surface p-4 sm:grid-cols-2"
    >
      <Field label="Monto total" value={formatMoney(cost.amount)} accent />
      <Field label="Forma de pago" value={cost.paymentMethod === "cash" ? "Efectivo" : "Crédito"} />
      <Field
        label="Fecha del registro"
        value={occurredAtDate.toLocaleDateString("es-CR", {
          day: "numeric",
          month: "short",
          year: "numeric",
        })}
      />
      <Field label="Moneda" value={cost.currencyCode} />
      <div className="sm:col-span-2">
        <Field label="Descripción" value={cost.description} multiline />
      </div>
    </section>
  );
}

function Field({
  label,
  value,
  accent,
  multiline,
  mono,
}: { label: string; value: string; accent?: boolean; multiline?: boolean; mono?: boolean }) {
  return (
    <div>
      <div className="text-[11px] text-app-ink-3 uppercase tracking-wide">{label}</div>
      <div
        className={
          accent
            ? "mt-0.5 font-semibold text-app-ink-1 text-xl tabular-nums"
            : multiline
              ? "mt-0.5 whitespace-pre-wrap text-[14px] text-app-ink-1"
              : mono
                ? "mt-0.5 font-mono text-[14px] text-app-ink-1"
                : "mt-0.5 text-[14px] text-app-ink-1"
        }
      >
        {value}
      </div>
    </div>
  );
}

function Breadcrumb() {
  return (
    <nav aria-label="Migas de pan" className="flex items-center gap-2 text-[12px] text-app-ink-3">
      <Link to="/app/costos" className="inline-flex items-center gap-1 hover:text-app-ink-1">
        <ChevronLeft className="h-3.5 w-3.5" aria-hidden />
        <span>Costos</span>
      </Link>
    </nav>
  );
}

function CostNotFound() {
  return (
    <>
      <title>Costo no encontrado · HATO</title>
      <Breadcrumb />
      <div className="mt-12 flex flex-col items-center gap-4 text-center">
        <h1 className="font-medium text-2xl text-app-ink-1 tracking-tight">Costo no encontrado</h1>
        <p className="max-w-sm text-app-ink-3 text-sm">El costo no existe o fue eliminado.</p>
        <Link
          to="/app/costos"
          className="rounded-xl border border-app-border bg-app-surface px-4 py-2 font-medium text-[13px] text-app-ink-1 hover:bg-app-surface-2"
        >
          Volver a Costos
        </Link>
      </div>
    </>
  );
}

function DetailSkeleton() {
  return (
    <div className="mt-4 space-y-4">
      <div className="h-10 w-64 animate-pulse rounded-xl bg-app-surface-2" />
      <div className="h-32 animate-pulse rounded-2xl bg-app-surface-2" />
      <div className="h-64 animate-pulse rounded-2xl bg-app-surface-2" />
    </div>
  );
}
